import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { BanknoteIcon, CheckIcon, ClockIcon, CreditCardIcon, DownloadIcon, LoaderCircleIcon, LockIcon, XIcon } from 'lucide-react';
import { savedCard } from '../../data/loisirs';
import { permitCashSteps, type PermitApplication } from '../../data/permis';
interface PermitFeeDialogProps {
  permit: PermitApplication | null;
  onClose: () => void;
  onPaid: (method: 'carte' | 'comptoir') => void;
}
type Stage = 'choix' | 'carte' | 'traitement' | 'paye' | 'comptoir';
const primaryButton = 'inline-flex items-center justify-center gap-2 rounded-full bg-ville-navy px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2';
const secondaryButton = 'inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2.5 text-sm font-bold text-ville-navy transition-colors duration-150 ease-ville hover:border-ville-indigo hover:bg-ville-indigo-soft hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo';
export function PermitFeeDialog({
  permit,
  onClose,
  onPaid
}: PermitFeeDialogProps) {
  const [stage, setStage] = useState<Stage>('choix');
  const timerRef = useRef<number | null>(null);
  useEffect(() => {
    if (!permit) {
      setStage('choix');
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previous;
    };
  }, [permit, onClose]);
  useEffect(() => () => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
  }, []);
  const pay = () => {
    setStage('traitement');
    timerRef.current = window.setTimeout(() => {
      setStage('paye');
      onPaid('carte');
    }, 1300);
  };
  const code = permit ? `BRO-${permit.reference.replace('#PER-', '')}-P` : '';
  return <AnimatePresence data-id="element-2495" data-mp="ga06udd1dph8">
      {permit && <motion.div className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: [0.23, 1, 0.32, 1]
    }} data-id="element-2496" data-mp="bb6uio9hk4in">
          <button type="button" aria-label="Fermer" onClick={onClose} className="absolute inset-0 bg-ville-navy/45" data-id="element-2497" data-mp="z8cjwa72jji3" />

          <motion.div role="dialog" aria-modal="true" aria-label="Paiement des frais de permis" initial={{
        y: 24,
        opacity: 0,
        scale: 0.98
      }} animate={{
        y: 0,
        opacity: 1,
        scale: 1
      }} exit={{
        y: 16,
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-white p-6 shadow-2xl sm:rounded-3xl sm:p-8" data-id="element-2498" data-mp="u7outu2ehcqi">
            <div className="flex items-start justify-between gap-4" data-id="element-2499" data-mp="249vmxezgfpap">
              <div className="min-w-0" data-id="element-2500" data-mp="2986wdolu7xse">
                <p className="text-xs font-bold uppercase tracking-wider text-ville-indigo" data-id="element-2501" data-mp="2d87ebj85ar47">
                  Demande acceptée · frais exigibles
                </p>
                <h2 className="mt-1 text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-2502" data-mp="2i5hb5jxv6522">
                  {stage === 'paye' ? 'Permis émis' : `${permit.fee} à payer`}
                </h2>
                <p className="mt-1 text-sm text-slate-600" data-id="element-2503" data-mp="2rwk604079nsw">
                  {permit.title} · {permit.reference}
                </p>
              </div>
              <button type="button" onClick={onClose} aria-label="Fermer" className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-slate-100 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-2504" data-mp="30xa115hmza5k">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-2505" data-mp="788xpqmkpkca" />
              </button>
            </div>

            <div className="mt-5" data-id="element-2506" data-mp="3fnaf4ko3mfyb">
              {stage === 'choix' && <div data-id="element-2507" data-mp="3fpn4vk03lulx">
                  <p className="flex items-start gap-2 rounded-2xl bg-ville-sand px-4 py-3 text-[13px] text-slate-600" data-id="element-2508" data-mp="3ap8x832esmof">
                    <ClockIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" data-id="element-2509" data-mp="3opjnp4zt3z92" />
                    Le permis est émis dès le paiement. À acquitter avant le{' '}
                    {permit.feeDueOn ?? 'délai indiqué par la Ville'}, sinon la demande acceptée devient
                    caduque.
                  </p>

                  <p className="mt-5 text-[15px] font-bold text-ville-navy" data-id="element-2510" data-mp="z19a6wzi21u1">Comment souhaitez-vous payer ?</p>
                  <div className="mt-3 space-y-3" data-id="element-2511" data-mp="17ijm9kyrvekv">
                    <button type="button" onClick={() => setStage('carte')} className="flex w-full items-start gap-3 rounded-2xl border border-slate-200 p-4 text-left transition-colors duration-150 ease-ville hover:border-ville-indigo hover:bg-ville-indigo-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-2512" data-mp="1bik31c9cg1v3">
                      <CreditCardIcon className="mt-0.5 h-5 w-5 shrink-0 text-ville-indigo" aria-hidden="true" data-id="element-2513" data-mp="1v4yhary19g00" />
                      <span data-id="element-2514" data-mp="1gf12h2lzu2nh">
                        <span className="block text-sm font-extrabold text-ville-navy" data-id="element-2515" data-mp="1lfezlpm23s6q">Carte en ligne</span>
                        <span className="block text-sm text-slate-600" data-id="element-2516" data-mp="2959bo4yjxcpx">
                          Permis émis immédiatement · certificat téléchargeable
                        </span>
                      </span>
                    </button>
                    <button type="button" onClick={() => setStage('comptoir')} className="flex w-full items-start gap-3 rounded-2xl border border-slate-200 p-4 text-left transition-colors duration-150 ease-ville hover:border-ville-indigo hover:bg-ville-indigo-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-2517" data-mp="2nrgghelshjqi">
                      <BanknoteIcon className="mt-0.5 h-5 w-5 shrink-0 text-ville-indigo" aria-hidden="true" data-id="element-2518" data-mp="24v6qpu1iz4qo" />
                      <span data-id="element-2519" data-mp="1v3auypmwvqpm">
                        <span className="block text-sm font-extrabold text-ville-navy" data-id="element-2520" data-mp="1v4lxyn8j8ufo">
                          Au comptoir de l’urbanisme
                        </span>
                        <span className="block text-sm text-slate-600" data-id="element-2521" data-mp="1m2meloa1pe9k">
                          Comptant, débit ou chèque · permis remis sur place
                        </span>
                      </span>
                    </button>
                  </div>
                </div>}

              {stage === 'carte' && <div data-id="element-2522" data-mp="1jinirduuso5">
                  <dl className="rounded-2xl border border-slate-200 bg-ville-sand p-4 text-sm" data-id="element-2523" data-mp="1j0jv9ov5c67">
                    <div className="flex items-center justify-between gap-4" data-id="element-2524" data-mp="5n87t4nhyo3d">
                      <dt className="text-slate-600" data-id="element-2525" data-mp="ak8hdpjk28z8">{permit.category}</dt>
                      <dd className="font-extrabold text-ville-navy" data-id="element-2526" data-mp="3a1g5lhim4r2b">{permit.fee}</dd>
                    </div>
                  </dl>

                  <div className="mt-4 flex items-center gap-3 rounded-2xl border border-ville-indigo bg-ville-indigo-soft p-4" data-id="element-2527" data-mp="3e2rwrrh4xkcm">
                    <CreditCardIcon className="h-5 w-5 shrink-0 text-ville-indigo" aria-hidden="true" data-id="element-2528" data-mp="3izicuesxwfy1" />
                    <div className="min-w-0" data-id="element-2529" data-mp="2qcnvvrvbzypb">
                      <p className="text-sm font-bold text-ville-navy" data-id="element-2530" data-mp="2qbmgxcb72s2b">
                        {savedCard.brand} •••• {savedCard.last4}
                      </p>
                      <p className="text-xs text-slate-500" data-id="element-2531" data-mp="1oo6ivdv57qtr">Carte enregistrée · expire {savedCard.expiry}</p>
                    </div>
                  </div>

                  <p className="mt-4 flex items-center gap-1.5 text-xs text-slate-500" data-id="element-2532" data-mp="1jr6dl564as3p">
                    <LockIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-2533" data-mp="1xqo7fyy9svjx" />
                    Paiement traité par la passerelle sécurisée de la Ville de Brossard.
                  </p>

                  <div className="mt-6 flex items-center justify-between gap-4" data-id="element-2534" data-mp="27lzjuam7hqoi">
                    <button type="button" onClick={() => setStage('choix')} className="text-sm font-semibold text-slate-500 transition-colors duration-150 ease-ville hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-2535" data-mp="22n68anqhc9qz">
                      Retour
                    </button>
                    <button type="button" onClick={pay} className={primaryButton} data-id="element-2536" data-mp="2lfqp36swmoef">
                      Payer {permit.fee}
                    </button>
                  </div>
                </div>}

              {stage === 'traitement' && <div className="py-10 text-center" data-id="element-2537" data-mp="2vzsvz5invcmk">
                  <LoaderCircleIcon className="mx-auto h-8 w-8 animate-spin text-ville-indigo" aria-hidden="true" data-id="element-2538" data-mp="2vz9f7s4qyua4" />
                  <p className="mt-4 text-sm font-semibold text-ville-navy" role="status" data-id="element-2539" data-mp="2m9ptmepngmbk">
                    Traitement du paiement et émission du permis…
                  </p>
                </div>}

              {stage === 'paye' && <div data-id="element-2540" data-mp="2c9yzie5049c0">
                  <span className="grid h-12 w-12 place-items-center rounded-full bg-ville-mist text-ville-deep" aria-hidden="true" data-id="element-2541" data-mp="2han09rj184jf">
                    <CheckIcon className="h-6 w-6" data-id="element-2542" data-mp="289ynt19aec8n" />
                  </span>
                  <h3 className="mt-3 text-lg font-extrabold text-ville-navy" data-id="element-2543" data-mp="28cb5ddb4ukl6">
                    Frais payés — permis {permit.reference} émis
                  </h3>
                  <p className="mt-1 text-sm text-slate-600" data-id="element-2544" data-mp="32tlk6z3iizi1">
                    Le certificat est déposé dans les fichiers du dossier. Affichez-le sur le chantier pendant
                    toute la durée des travaux.
                  </p>
                  <button type="button" className={`mt-5 ${secondaryButton}`} data-id="element-2545" data-mp="3hhos4e148l9h">
                    <DownloadIcon className="h-4 w-4" aria-hidden="true" data-id="element-2546" data-mp="3hjt4zlgzsulw" />
                    Télécharger le permis
                  </button>
                  <button type="button" onClick={onClose} className={`mt-4 w-full ${primaryButton}`} data-id="element-2547" data-mp="37sf0eza6p4lw">
                    Terminer
                  </button>
                </div>}

              {stage === 'comptoir' && <div data-id="element-2548" data-mp="49lr4yofv1xj">
                  <div className="rounded-2xl border-2 border-ville-navy bg-white p-4 text-center" data-id="element-2549" data-mp="493nhgzg5lfl">
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500" data-id="element-2550" data-mp="93pxpgtsl7y3">
                      Code de paiement
                    </p>
                    <p className="mt-1 text-2xl font-extrabold tracking-[0.18em] text-ville-navy" data-id="element-2551" data-mp="ee215bf8lotf">{code}</p>
                    <p className="mt-1 text-xs text-slate-500" data-id="element-2552" data-mp="s56yu7z1o714">
                      À payer avant le {permit.feeDueOn ?? 'délai indiqué par la Ville'}
                    </p>
                  </div>

                  <ol className="mt-5 space-y-4" data-id="element-2553" data-mp="3vnqr3ahq3cvx">
                    {permitCashSteps.map((step, index) => <li key={step.title} className="flex gap-3" data-id="element-2554" data-mp="3rng88x8u0rzn" data-mpi={index}>
                        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-ville-navy text-xs font-bold text-white" aria-hidden="true" data-id="element-2555" data-mp="9h1w93u4is3w">
                          {index + 1}
                        </span>
                        <div className="min-w-0" data-id="element-2556" data-mp="1fv1awnbjhrkb">
                          <p className="text-sm font-bold text-ville-navy" data-id="element-2557" data-mp="1nwseppqxwe9j">{step.title}</p>
                          <p className="mt-0.5 text-sm text-slate-600" data-id="element-2558" data-mp="1nz4sdqpo02gs">{step.detail}</p>
                        </div>
                      </li>)}
                  </ol>

                  <div className="mt-6 flex items-center justify-between gap-4" data-id="element-2559" data-mp="1wvds76hk133g">
                    <button type="button" onClick={() => setStage('choix')} className="text-sm font-semibold text-slate-500 transition-colors duration-150 ease-ville hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-2560" data-mp="21w1j7s5s9jxo">
                      Retour
                    </button>
                    <button type="button" onClick={() => {
                onPaid('comptoir');
                onClose();
              }} className={primaryButton} data-id="element-2561" data-mp="1iz0apxd0t1oz">
                      J’ai noté le code
                    </button>
                  </div>
                </div>}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}